import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dashboardApi, waitingApi, workspaceApi } from '../../api';
import { BRAND_NAME } from '../../config';
import { t, tk } from '../../i18n';
import { cx } from '../../lib/cx';
import { useAuth } from '../../stores/auth';
import { useTheme } from '../../stores/theme';
import { useWorkspace } from '../../stores/workspace';
import { Avatar } from '../ui';
import { IconLogout, IconMenu, IconTheme } from '../ui/Icon';
import { DomumMark } from './DomumMark';
import { canSee, type Section } from './nav';
import { partnerCompanyName } from './tenant';
import { menuFor, menuItemFor, myWorkspaceKeys, WORKSPACE_BY_KEY, workspaceOfPath } from './workspaces';

export function Sidebar() {
  const { theme, toggle } = useTheme();
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const role = me?.role;
  const partner = partnerCompanyName(me);
  const keys = myWorkspaceKeys(me);
  const stored = useWorkspace((s) => s.current);
  const setCurrent = useWorkspace((s) => s.setCurrent);
  const current = stored && keys.includes(stored) ? stored : (keys[0] ?? null);
  const items = menuFor(current, me);
  const active = menuItemFor(current, pathname + search, items)?.to;
  const title = current ? t(WORKSPACE_BY_KEY[current].titleKey) : null;

  // сторінка належить іншому моєму простору (перейшли посиланням) — тихо перемикаємо простір
  useEffect(() => {
    if (!current || menuItemFor(current, pathname + search, menuFor(current, me))) return;
    const other = workspaceOfPath(pathname + search, keys);
    if (other && other !== current) void setCurrent(other, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, current]);

  // лічильники в меню: лише для тих, хто бачить відповідні розділи
  const dash = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.get,
    refetchInterval: 60_000,
    retry: false,
    enabled: canSee(role, 'requests'),
  });
  const today = useQuery({ queryKey: ['today'], queryFn: workspaceApi.today, refetchInterval: 60_000, retry: false, enabled: canSee(role, 'today') });
  const waiting = useQuery({ queryKey: ['waiting', 'summary'], queryFn: waitingApi.summary, refetchInterval: 120_000, retry: false, enabled: canSee(role, 'waiting') });
  const counts: Partial<Record<Section, number | null | undefined>> = {
    // непрочитані розмови рахуємо лише тим, хто бачить «Вхідні» (бухгалтеру /conversations недоступні)
    requests: dash.data ? (dash.data.leads?.open ?? 0) + (canSee(role, 'inbox') ? (dash.data.inbox?.unread ?? 0) : 0) : null,
    tasks: today.data?.counts.tasks_open,
    // запити на відпустку чекають погодження керівника
    planner: today.data?.burning.filter((b) => b.kind === 'absence_request').length,
    // «Чого чекаємо»: лише прострочене — щоб лічильник означав «треба дзвонити»
    waiting: waiting.data?.overdue,
  };

  return (
    <aside className="md:flex-[0_0_244px] md:w-[244px] w-full bg-side text-side-fg border-b md:border-b-0 md:border-r border-line px-3 py-4 md:py-5 flex flex-col gap-4 md:gap-3 md:sticky md:top-0 md:h-screen md:overflow-hidden">
      <div className="flex justify-between items-center gap-4 px-3 md:pb-3">
        {partner ? (
          // партнерська компанія: своя назва замість знака DOMUM
          <Link to="/" className="min-w-0 font-bold text-[17px] leading-tight truncate" title={partner} aria-label={partner}>
            {partner}
          </Link>
        ) : (
          <Link to="/" className="inline-flex items-center gap-2.5" aria-label={BRAND_NAME}>
            <DomumMark className="h-[25px] w-5 shrink-0" />
            <span className="font-wordmark text-[17px] leading-none uppercase" style={{ letterSpacing: '0.22em' }}>
              {BRAND_NAME}
            </span>
          </Link>
        )}
        <button
          type="button"
          className="md:hidden inline-flex items-center gap-2 min-h-10 px-3 rounded-lg text-sm text-side-mute hover:bg-side-hov"
          aria-expanded={open}
          aria-controls="main-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <IconMenu />
          {t('nav.menu')}
        </button>
      </div>
      {/* меню прокручується саме, а блок акаунта з «Вийти» завжди видно внизу — навіть на невисокому екрані ноутбука */}
      <nav
        id="main-nav"
        aria-label={t('nav.label')}
        className={cx('flex-col gap-0.5 md:flex md:flex-1 md:min-h-0 md:overflow-y-auto md:-mx-3 md:px-3', open ? 'flex' : 'hidden')}
      >
        {title && <div className="px-3 pt-2.5 pb-0.5 text-xs font-semibold text-lab truncate">{title}</div>}
        {items.map((it) => {
          const isActive = active === it.to;
          const n = counts[it.section];
          return (
            <Link
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              aria-current={isActive ? 'page' : undefined}
              // явна назва посилання для читачів екрана (разом із лічильником, якщо він є)
              aria-label={n ? `${t(it.key)}: ${it.section === 'planner' ? t('notif.plannerCount', { n }) : n}` : t(it.key)}
              className={cx(
                'flex justify-between items-center gap-2.5 min-h-8 px-3 rounded-lg text-base transition-colors',
                isActive ? 'bg-side-act text-side-act-fg font-semibold' : 'text-side-mute hover:bg-side-hov hover:text-side-fg',
              )}
            >
              <span>{t(it.key)}</span>
              {n ? (
                <span
                  className={cx('text-xs tabular-nums', isActive ? 'opacity-70' : 'text-mute')}
                  // підпис, щоб було зрозуміло, що рахує цифра (біля «Планування» — запити на відсутність)
                  title={it.section === 'planner' ? t('notif.plannerCount', { n }) : undefined}
                >
                  {n}
                  {it.section === 'planner' && <span className="sr-only"> · {t('notif.plannerCount', { n })}</span>}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>
      <div className={cx('md:shrink-0 flex-col gap-0.5 text-sm md:flex border-t border-line-soft pt-3', open ? 'flex' : 'hidden')}>
        {/* акаунт, тема і «Вийти» в один рядок — щоб меню вміщалось на екрані ноутбука без прокрутки */}
        <div className="flex items-center gap-1">
          {me && (
            <Link
              to="/settings/security"
              className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-side-hov"
              onClick={() => setOpen(false)}
              aria-label={`${me.user?.full_name || me.user?.email} · ${tk('role', me.role)} — ${t('nav.profile')}`}
            >
              <Avatar name={me.user?.full_name || me.user?.email} className="!w-8 !h-8 !text-xs !bg-side-act !text-side-act-fg" />
              <span className="flex flex-col min-w-0">
                <b className="font-semibold text-sm truncate">{me.user?.full_name || me.user?.email}</b>
                <span className="text-xs text-mute">{tk('role', me.role)}</span>
              </span>
            </Link>
          )}
          <button
            type="button"
            className="w-9 h-9 grid place-items-center rounded-lg text-side-mute hover:text-side-fg hover:bg-side-hov flex-none"
            onClick={toggle}
            aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
            title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
          >
            <IconTheme />
          </button>
          <button
            type="button"
            className="w-9 h-9 grid place-items-center rounded-lg text-side-mute hover:text-side-fg hover:bg-side-hov flex-none"
            onClick={() => void logout()}
            aria-label={t('nav.logout')}
            title={t('nav.logout')}
          >
            <IconLogout />
          </button>
        </div>
      </div>
    </aside>
  );
}
